// Auth actions the screens call. Tiny composition: hit an auth
// endpoint, write the session into the store, return for the caller
// to react to. Mirrors apps/web-svelte/src/lib/auth/actions.ts.

import type { LoginRequest, TokenPairResponse, TotpEnrollRequest } from "@bitacorapro/api-types";
import { authApi } from "../api/endpoints/auth";
import type { Session } from "../api/session";
import { clearAuth, currentSession, setSession } from "../api/sessionStore";

function asSession(pair: TokenPairResponse): Session {
  return pair;
}

// Discriminated result for /auth/login (ADR-0024). The Login screen
// branches on `kind` to either complete (session stored) or render
// the TOTP step (code field or QR + code field).
export type LoginResult =
  | { kind: "complete"; session: Session }
  | { kind: "totp-required" }
  | { kind: "totp-enrollment"; qrSvg: string; provisioningUri: string };

export async function login(body: LoginRequest): Promise<LoginResult> {
  const res = await authApi.login(body);
  if ("kind" in res && res.kind === "totp-required") {
    return { kind: "totp-required" };
  }
  if ("kind" in res && res.kind === "totp-enrollment") {
    return {
      kind: "totp-enrollment",
      qrSvg: res.qrSvg,
      provisioningUri: res.provisioningUri,
    };
  }
  // TokenPairResponse — happy path, no `kind` field on the wire.
  const session = asSession(res);
  setSession(session);
  return { kind: "complete", session };
}

// POST /auth/totp/enroll — finishes the first-login enrollment for a
// platform admin and returns the JWT pair, which we install as the
// session immediately (same shape as a normal login).
export async function enrollTotp(body: TotpEnrollRequest): Promise<Session> {
  const pair = await authApi.enrollTotp(body);
  const session = asSession(pair);
  setSession(session);
  return session;
}

// Best-effort server-side logout — local clear is the source of truth
// from here, so a network failure on revoke doesn't keep the user
// signed in on this device.
export async function logout(): Promise<void> {
  const session = currentSession();
  if (session?.refreshToken) {
    try {
      await authApi.logout({ refreshToken: session.refreshToken });
    } catch {
      /* ignore */
    }
  }
  clearAuth();
}
